import { TellescopeModelFromName } from "@tellescope/tanstack-query";

type PartialMedication = Omit<Partial<TellescopeModelFromName<"enduser_medications">>, "enduserId" | "title"> & {
  title: string;
  enduserId: string;
};

type NestedObject = Record<string, unknown>;

const asNestedObject = (value: unknown): NestedObject | undefined =>
  value !== null && typeof value === "object" && !Array.isArray(value) ? { ...value } : undefined;

/**
 * The nested object at `key`, creating it when absent so the walk below always has somewhere to
 * write. Returns the object that was placed on `parent`, never a widened view of an existing value.
 */
const ensureNestedObject = (parent: NestedObject, key: string): NestedObject => {
  const nested = asNestedObject(parent[key]) ?? {};
  parent[key] = nested;
  return nested;
};

/**
 * Transform flat CSV data with dot notation into nested objects
 * Converts dot notation columns (e.g., "dispensing.quantity") into nested structures
 *
 * `title` is defaulted rather than asserted: the schema marks it required, so `validateCSV` has
 * already rejected any row missing it, but that guarantee lives in the schema rather than in this
 * function's input type.
 */
export const transformMedicationData = (flatData: unknown[], enduserId: string): PartialMedication[] => {
  return flatData.map((row) => {
    const source = asNestedObject(row);
    if (!source) return { enduserId, title: "" };

    const fields: NestedObject = {};

    Object.keys(source).forEach((key) => {
      const value = source[key];

      if (key.includes(".")) {
        // Handle nested fields (e.g., "dispensing.quantity" -> { dispensing: { quantity: value } })
        const parts = key.split(".");
        const leaf = parts.pop();
        const parent = parts.reduce<NestedObject>(
          (current, part) => (part ? ensureNestedObject(current, part) : current),
          fields
        );

        if (leaf) {
          parent[leaf] = value;
        }
      } else {
        fields[key] = value;
      }
    });

    const { title, ...rest } = fields;

    return { ...rest, enduserId, title: typeof title === "string" ? title : "" };
  });
};
